import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { ColorPallet } from "../utils/colorPallet";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import AllTable from "../components/commontable/table";
import { getBatchData } from "../features/batch/batchSlice";
import WarningIcon from "../components/IconComp/warningIcon";
import WarningIconYellow from "../components/IconComp/warningIconYellow";
import { useLocation } from "react-router-dom";
import { EditBatchToolbar } from "../components/inventory/batch/batch-edit-toolbar";
import LoadingSpinner from "../components/common/Loading";
import { OrderPurchaseHeader } from "../utils/constants";
import usePermissions from "../hooks/usePermissions";
import { getAllStaffPermissions } from "../features/staff/staffSlice";
import HideIcon from "../components/IconComp/hideIcon";
import { HideBatchModel } from "../components/inventory/batch/batch-hide-model";
import AppSnackbar from "../components/common/AppSnackbar";
import { getMedicineData } from "../features/medicine/medicineSlice";
import { getNonMedicalData } from "../features/nonMedical/nonMedicalSlice";
import SearchIcon from "../components/IconComp/searchIcon";

const OrderPurchase = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isFromExpireMedBatch =
    location.state && location.state.fromExpireMedBatch;

  const [showExpired, setShowExpired] = useState(false);
  const [showNearExpiry, setShowNearExpiry] = useState(false);
  const [batchData, setBatchData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expireMedCount, setExpireMedCount] = useState<any>();
  const [nearExpireMedCount, setNearExpireMedCount] = useState<any>();
  //pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(""); // Debounce
  //const rowData = useAppSelector((state) => state?.batch);
  const [selectedDate, setSelectedDate] = useState<string>(); // Set the current date by default
  const pharmacyId = localStorage?.getItem("pharmacyId");
  const isPermissions = usePermissions();
  const Account_Type = parseInt(
    localStorage.getItem("pharmacy_account_type") || "0",
    10
  );
  const [selectedId, setSelectedId] = useState<any>(undefined);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { staffPermissionsPayload }: any = useAppSelector(
    (state) => state?.staff
  );
  const pharmacy_Id: any = localStorage?.getItem("pharmacyId");
  const [addedByChecked, setAddedByChecked] = useState(false);
  const [updatedByChecked, setUpdatedByChecked] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [apiError, setApiError] = useState(false);
  const isFromResolve = location?.state && location?.state?.fromResolve;
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [showLowInStock, setShowLowInStock] = useState(false);
  const [lowInStockCount, setLowInStockCount] = useState<any>();
  const [outOfStockCount, setOutOfStockCount] = useState<any>();
  const [openModal, setOpenModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedType, setSelectedType] = useState<"medical" | "nonMedical">(
    "medical"
  );
  const medicineData = useAppSelector((state) => state?.medicine);
  const nonMedicalData = useAppSelector((state) => state?.nonMedical);
  const rowData = selectedType === "medical" ? medicineData : nonMedicalData;
  const handleOpenModal = (id: number) => {
    setSelectedBatchId(id);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setApiError(false);
    setOpenModal(false);
  };

  // Debounce effect for search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Adjust debounce delay as needed
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleChange = (event: any) => {
    setSelectedId(event?.target?.value);
    setPage(0);
  };

  const handleAddedByChange = (event: any) => {
    setAddedByChecked(event.target.checked);
    setPage(0);
  };

  const handleUpdatedByChange = (event: any) => {
    setUpdatedByChecked(event.target.checked);
    setPage(0);
  };

  const GetAllStaffPermissions = useCallback(() => {
    dispatch(getAllStaffPermissions(pharmacy_Id)).then((res: any) => {
      if (res?.payload?.success === true) {
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (pharmacy_Id) {
      GetAllStaffPermissions();
    }
  }, [pharmacy_Id, GetAllStaffPermissions]);

  const options = staffPermissionsPayload
    ? staffPermissionsPayload?.map((item: any) => ({
        id: item?.staff?.id,
        name:
          item?.staff?.name.charAt(0).toUpperCase() +
          item?.staff?.name.slice(1).toLowerCase(),
      }))
    : [];

  const getMedicine = useCallback(async () => {
    const dto: any = {
      page: page + 1, // API pages are 1-indexed
      limit: rowsPerPage,
      search: debouncedSearchQuery,
      lowStock: true,
      outOfStock: true,
    };
    try {
      setIsLoading(true); // Start loading
      const action =
        selectedType === "medical" ? getMedicineData : getNonMedicalData;
      const res = await dispatch(action({ pharmacy_id: pharmacyId, dto }));
      if (res.payload) {
        setTotalCount(res?.payload?.pagination?.totalItems || 0); // Ensure number type
        setOutOfStockCount(res?.payload?.count?.outOfStockCount || 0); // Ensure number type
        setLowInStockCount(res?.payload?.count?.lowStockCount || 0); // Ensure number type
      }
    } catch (error) {
      console.error("Error fetching manufacturer data:", error);
    } finally {
      setIsLoading(false); // Stop loading
      // Only focus if there's an active search query
      if (debouncedSearchQuery) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }
  }, [
    selectedType,
    dispatch,
    pharmacyId,
    page,
    rowsPerPage,
    debouncedSearchQuery,
    showOutOfStock,
    showLowInStock,
  ]); // Add dependencies as needed

  // Preserve cursor position while typing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = e.target; // Save cursor position
    setSearchQuery(e.target.value);
    setPage(0);
    // Restore cursor position after state update
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    }, 0);
  };

  const hasAccess =
    Account_Type === 0 || (isPermissions?.view && Account_Type === 1);

  useEffect(() => {
    if (Account_Type === 0 || (isPermissions?.view && Account_Type === 1)) {
      getMedicine();
    } else {
      setIsLoading(false);
    }
  }, [getMedicine, isPermissions?.view, Account_Type]);

  const tableRow = rowData?.medicineDetails?.map((item: any) => {
    return {
      "Medicine Name": item?.medicine_name,
      Dose: ((d?: string) => {
        const cleaned = (d ?? "").replace(/\bnull\b/gi, "").trim();
        return cleaned || "-";
      })(item?.dose),
      "Total Quantity": item?.total_quantity,
      "R. Quantity": (
        <>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Box>
              {item?.shortage === "Yes" ? (
                <WarningIconYellow />
              ) : item?.total_quantity - item?.quantity_sold === 0 ? (
                <WarningIcon />
              ) : null}
            </Box>
            <Box>
              {(item?.total_quantity - item?.quantity_sold)?.toLocaleString(
                "en-IN"
              )}
            </Box>
          </Box>
        </>
      ),
      "Manufacturer Name": item?.manufacturer_name,
      Actions: (
        <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          {/* <DeleteIcon />  */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              padding: "5px 0px",
            }}
          >
            {(isPermissions?.edit && Account_Type === 1) ||
            Account_Type === 0 ? (
              <>
                <EditBatchToolbar
                  batch={item}
                  isEdit={true}
                  order={true}
                  type={"restock"}
                  setExpireMedCount={setExpireMedCount}
                  setNearExpireMedCount={setNearExpireMedCount}
                  getMedicine={getMedicine}
                />

                <EditBatchToolbar
                  batch={item}
                  isEdit={false}
                  order={true}
                  type={"edit"}
                  setExpireMedCount={setExpireMedCount}
                  setNearExpireMedCount={setNearExpireMedCount}
                  getMedicine={getMedicine}
                />
              </>
            ) : null}
          </Box>
        </Box>
      ),
    };
  });

  const tableRowData = hasAccess ? tableRow : [];
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  return (
    <>
      <Box
        sx={{
          margin: 1,
          backgroundColor: ColorPallet?.bgcolor,
          // marginTop: "10px",
          // padding: "10px",
          borderRadius: "5px",
          "@media screen and (max-width: 1024px)": {
            padding: "7px",
            margin: 2,
          },
          "@media screen and (max-width: 768px)": {
            padding: "5px",
            margin: 1,
          },
          "@media screen and (max-width: 480px)": {
            padding: "3px",
            margin: 1,
          },
          minHeight: {
            xs: "auto",
            sm: "80vh",
          },
          // my: "1.2%",
        }}
      >
        <Box
          sx={{
            mt: 2,
            px: 1,
            paddingBottom: 1,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <RadioGroup
                row
                value={selectedType}
                onChange={(e) =>
                  setSelectedType(e.target.value as "medical" | "nonMedical")
                }
              >
                <FormControlLabel
                  value="medical"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: "#25476A",
                        "&.Mui-checked": {
                          color: "#25476A",
                        },
                      }}
                    />
                  }
                  label="Medical"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "16px",
                      fontWeight: 400,
                    },
                  }}
                />
                <FormControlLabel
                  value="nonMedical"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: "#25476A",
                        "&.Mui-checked": {
                          color: "#25476A",
                        },
                      }}
                    />
                  }
                  label="Non Medical"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "16px",
                      fontWeight: 400,
                    },
                  }}
                />
              </RadioGroup>
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextField
                inputRef={searchInputRef} // Assign ref to maintain focus
                placeholder="Search..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "200px" }}
              />
            </Box>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh", // Set height to full viewport height
              }}
            >
              <LoadingSpinner />
            </Box>
          ) : (
            <AllTable
              headerData={OrderPurchaseHeader}
              rowData={tableRowData}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              hasAccess={hasAccess}
            />
          )}
        </Box>
      </Box>

      {/* <HideBatchModel /> */}
      <HideBatchModel
        open={openModal}
        onClose={handleCloseModal}
        setOpenSnackbar={setOpenSnackbar}
        selectedBatchId={selectedBatchId}
        setApiError={setApiError}
        apiError={apiError}
      />
      <AppSnackbar
        message="Medicine hidden successfully!"
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
      />
    </>
  );
};
export default OrderPurchase;
